/**
 * Connector instance detail page component.
 * View and edit connector instance configuration.
 *
 * Migrated to CoOrdio Cortex shell chrome: `ShellHeader`, `PageContainer`, `FormSection`,
 * `InlineErrorPanel`, `LoadingSkeleton`, custom tab strip (not MUI Tabs), `@coordio/ui` Button.
 */

// React Imports
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Trash2 as DeleteIcon, Copy as DuplicateIcon, Play as TestIcon } from 'lucide-react';
import { Button, Chip, Input, ShellHeader, Switch, Textarea } from '@coordio/ui';
import { FormSection, InlineErrorPanel, LoadingSkeleton, PageContainer } from '@coordio/ui/layout';
import { connectorService } from '../services/connectorService';
import { useNotificationStore } from '../store/notificationStore';
import { useConfirmDialog } from '../components/common/ConfirmDialog';

import { ConnectionStatusIndicator } from '../components/connectors/ConnectionStatusIndicator';
import { OAuthConnectButton } from '../components/connectors/OAuthConnectButton';
import { ConnectorTestPanel } from '../components/connectors/ConnectorTestPanel';
import { SettingsForm } from '../components/connectors/SettingsForm';
import { ConnectorConfigRenderer } from '../components/connectors/ConnectorConfigRenderer';
import { CapabilityList } from '../components/connectors/CapabilityList';
import { RateLimitStatusCard } from '../components/connectors/RateLimitStatusCard';
import type {
  ConnectorInstanceDetailDto,
  UpdateConnectorInstanceRequest,
  RateLimitStatus,
  ConnectorUsageStats,
} from '../types/connector';

/**
 * Connector instance detail page component.
 */
export const ConnectorInstanceDetailPage = () => {
  const { instanceId } = useParams<{ instanceId: string }>();
  const navigate = useNavigate();
  const { addNotification } = useNotificationStore();
  const confirm = useConfirmDialog();

  const [instance, setInstance] = useState<ConnectorInstanceDetailDto | null>(null);
  const [rateLimitStatus, setRateLimitStatus] = useState<RateLimitStatus | null>(null);
  const [usageStats, setUsageStats] = useState<ConnectorUsageStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState('overview');

  // Form data
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [rateLimitPerMinute, setRateLimitPerMinute] = useState<number | ''>('');
  const [rateLimitPerHour, setRateLimitPerHour] = useState<number | ''>('');
  const [rateLimitPerDay, setRateLimitPerDay] = useState<number | ''>('');
  const [settingsJson, setSettingsJson] = useState('{}');
  const [enabledCapabilityIds, setEnabledCapabilityIds] = useState<string[]>([]);

  useEffect(() => {
    if (instanceId) {
      loadInstance();
      loadRateLimitStatus();
      loadUsageStats();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [instanceId]);

  const loadInstance = async () => {
    if (!instanceId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await connectorService.getConnectorInstance(instanceId);
      setInstance(data);
      setName(data.name);
      setDescription(data.description || '');
      setIsActive(data.isActive);
      setRateLimitPerMinute(data.rateLimitPerMinute || '');
      setRateLimitPerHour(data.rateLimitPerHour || '');
      setRateLimitPerDay(data.rateLimitPerDay || '');
      
      try {
        const settings = await connectorService.getConnectorSettings(instanceId);
        setSettingsJson(settings.settingsJson || '{}');
      } catch {
        // Settings might not exist yet
      }
      
      if (data.connector) {
        setEnabledCapabilityIds(data.connector.capabilities.filter((c) => c.isActive).map((c) => c.id));
      }
    } catch (err: any) {
      const msg = err.response?.data?.message || 'Failed to load connector instance';
      setError(msg);
      addNotification(msg, 'error');
    } finally {
      setLoading(false);
    }
  };

  const loadRateLimitStatus = async () => {
    if (!instanceId) return;
    try {
      const status = await connectorService.getRateLimitStatus(instanceId);
      setRateLimitStatus(status);
    } catch {
      // Rate limit status might not be available
    }
  };

  const loadUsageStats = async () => {
    if (!instanceId) return;
    try {
      const stats = await connectorService.getConnectorUsageStats(instanceId);
      setUsageStats(stats);
    } catch {
      // Usage stats might not be available
    }
  };

  const handleSave = async () => {
    if (!instanceId) return;
    setSaving(true);
    setError(null);
    try {
      const request: UpdateConnectorInstanceRequest = {
        name: name.trim(),
        description: description.trim() || undefined,
        isActive,
        rateLimitPerMinute: rateLimitPerMinute ? Number(rateLimitPerMinute) : undefined,
        rateLimitPerHour: rateLimitPerHour ? Number(rateLimitPerHour) : undefined,
        rateLimitPerDay: rateLimitPerDay ? Number(rateLimitPerDay) : undefined,
        settingsJson: settingsJson || '{}',
      };

      await connectorService.updateConnectorInstance(instanceId, request);
      addNotification('Connector instance updated successfully', 'success');
      await loadInstance();
      await loadRateLimitStatus();
    } catch (err: any) {
      const msg = err.response?.data?.message || 'Failed to update connector instance';
      setError(msg);
      addNotification(msg, 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!instanceId) return;
    const ok = await confirm({
      title: 'Delete connector',
      message: `Are you sure you want to delete "${name}"?`,
      confirmText: 'Delete',
    });
    if (!ok) return;
    try {
      await connectorService.deleteConnectorInstance(instanceId);
      addNotification('Connector instance deleted successfully', 'success');
      navigate('/connectors');
    } catch (err: any) {
      addNotification(err.response?.data?.message || 'Failed to delete connector instance', 'error');
    }
  };

  const handleDuplicate = () => {
    if (instance?.connectorId) {
      navigate(`/connectors/${instance.connectorId}/instances/new`);
    }
  };

  if (loading) {
    return (
      <div className="connector-instance-detail">
        <ShellHeader title="Connector Instance" description="Loading…" data-testid="connector-instance-header" />
        <PageContainer data-testid="connector-instance-form-page">
          <LoadingSkeleton rows={6} data-testid="connector-instance-loading" />
        </PageContainer>
      </div>
    );
  }

  if (!instance) {
    return (
      <div className="connector-instance-detail">
        <ShellHeader
          title="Connector Instance"
          description="Instance not found."
          data-testid="connector-instance-header"
          actions={
            <Button type="button" variant="outline" size="sm" onClick={() => navigate('/connectors')} data-testid="back-connector-instance-btn">
              Back
            </Button>
          }
        />
        <PageContainer data-testid="connector-instance-form-page">
          <InlineErrorPanel message={error || 'Connector instance not found'} data-testid="connector-instance-error-panel" />
        </PageContainer>
      </div>
    );
  }

  const connectionStatus: 'Connected' | 'Disconnected' | 'Expired' | 'Error' | 'Unknown' =
    instance.isCredentialValid === true
      ? 'Connected'
      : instance.isCredentialValid === false
      ? 'Disconnected'
      : 'Unknown';

  const tabs = [
    { label: 'Overview', value: 'overview' },
    { label: 'Configuration', value: 'configuration' },
    { label: 'Settings', value: 'settings' },
    { label: 'Capabilities', value: 'capabilities' },
    { label: 'Rate Limits', value: 'rate-limits' },
    { label: 'Usage & Analytics', value: 'usage' },
    ...(instance.webhooks && instance.webhooks.length > 0
      ? [{ label: 'Webhooks', value: 'webhooks' }]
      : []),
  ];

  return (
    <div className="connector-instance-detail">
      <ShellHeader
        title={instance.name}
        description={instance.connector ? `${instance.connector.name} Instance` : undefined}
        data-testid="connector-instance-header"
        actions={
          <div className="flex items-center gap-2">
            <ConnectionStatusIndicator status={connectionStatus} />
            <Chip
              variant="outlined"
              color={instance.isActive ? 'green' : 'default'}
              label={instance.isActive ? 'Active' : 'Inactive'}
            />
            <Button type="button" variant="ghost" size="sm" className="gap-2" onClick={() => setActiveTab('configuration')} aria-label="Test Connection" data-testid="connector-instance-test-btn">
              <TestIcon className="h-4 w-4" />
            </Button>
            <Button type="button" variant="ghost" size="sm" className="gap-2" onClick={handleDuplicate} aria-label="Duplicate" data-testid="connector-instance-duplicate-btn">
              <DuplicateIcon className="h-4 w-4" />
            </Button>
            <Button type="button" variant="destructive" size="sm" className="gap-2" onClick={handleDelete} data-testid="delete-connector-instance-btn">
              <DeleteIcon className="h-4 w-4" />
              Delete
            </Button>
            <Button type="button" variant="outline" size="sm" onClick={() => navigate('/connectors')} data-testid="back-connector-instance-btn">
              Back
            </Button>
          </div>
        }
      />

      <PageContainer data-testid="connector-instance-form-page">
        {error && (
          <InlineErrorPanel
            message={error}
            data-testid="connector-instance-error-panel"
            action={<Button type="button" variant="ghost" size="sm" onClick={() => setError(null)}>Dismiss</Button>}
          />
        )}

        <div className="page-detail-layout__tabs" role="tablist" aria-label="Connector instance sections">
          {tabs.map((tab) => (
            <button
              key={tab.value}
              type="button"
              role="tab"
              aria-selected={activeTab === tab.value}
              className="page-detail-layout__tab"
              data-testid={`connector-instance-tab-${tab.value}`}
              onClick={() => setActiveTab(tab.value)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div role="tabpanel" className="page-detail-layout__panel" data-testid="connector-instance-panel-overview">
            <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
              <div className="md:col-span-8">
                <FormSection title="Instance Information" data-testid="connector-instance-section-instance-information">
                  <div className="flex flex-col gap-4">
                    <div>
                      <span className="text-xs text-[var(--color-text-secondary)]">Name</span>
                      <p className="text-sm font-medium">{instance.name}</p>
                    </div>
                    {instance.description && (
                      <div>
                        <span className="text-xs text-[var(--color-text-secondary)]">Description</span>
                        <p className="text-sm">{instance.description}</p>
                      </div>
                    )}
                    <div>
                      <span className="text-xs text-[var(--color-text-secondary)]">Connection Status</span>
                      <div className="mt-1">
                        <ConnectionStatusIndicator status={connectionStatus} />
                      </div>
                    </div>
                  </div>
                </FormSection>
              </div>
              <div className="md:col-span-4">
                <FormSection title="Quick Stats" data-testid="connector-instance-section-quick-stats">
                  {usageStats && (
                    <div className="flex flex-col gap-4">
                      <div>
                        <span className="text-xs text-[var(--color-text-secondary)]">Total Calls</span>
                        <p className="text-xl font-semibold">{usageStats.totalCalls.toLocaleString()}</p>
                      </div>
                      <div>
                        <span className="text-xs text-[var(--color-text-secondary)]">Success Rate</span>
                        <p className="text-xl font-semibold">
                          {usageStats.totalCalls > 0
                            ? ((usageStats.successfulCalls / usageStats.totalCalls) * 100).toFixed(1)
                            : 0}
                          %
                        </p>
                      </div>
                      <div>
                        <span className="text-xs text-[var(--color-text-secondary)]">Avg Duration</span>
                        <p className="text-xl font-semibold">{usageStats.averageDurationMs.toFixed(0)}ms</p>
                      </div>
                    </div>
                  )}
                </FormSection>
              </div>
            </div>
          </div>
        )}

        {/* Configuration Tab */}
        {activeTab === 'configuration' && (
          <div role="tabpanel" className="page-detail-layout__panel" data-testid="connector-instance-panel-configuration">
            <FormSection title="Basic Information" data-testid="connector-instance-section-basic-information">
              <div className="flex flex-col gap-6">
                <div className="flex flex-col gap-1">
                  <label className="text-sm font-medium" htmlFor="connector-instance-name">Instance Name</label>
                  <Input
                    id="connector-instance-name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    data-testid="connector-instance-name"
                  />
                </div>
                <div className="flex flex-col gap-1">
                  <label className="text-sm font-medium" htmlFor="connector-instance-description">Description</label>
                  <Textarea
                    id="connector-instance-description"
                    rows={3}
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    data-testid="connector-instance-description"
                  />
                </div>
                <label className="flex items-center gap-2">
                  <Switch checked={isActive} onCheckedChange={setIsActive} data-testid="connector-instance-isActive" />
                  <span className="text-sm">Active</span>
                </label>
              </div>
            </FormSection>

            <FormSection title="Authentication" data-testid="connector-instance-section-authentication">
              {instance.authenticationType === 'OAuth2' && (
                <OAuthConnectButton
                  instanceId={instanceId!}
                  connectorName={instance.connector?.name || 'Connector'}
                  isConnected={connectionStatus === 'Connected'}
                  onConnected={() => {
                    loadInstance();
                    addNotification('Successfully connected', 'success');
                  }}
                  onError={(err) => setError(err)}
                />
              )}
              {instance.authenticationType !== 'OAuth2' && (
                <div className="rounded-lg bg-[var(--color-bg-inset)] p-4 text-[var(--color-text-secondary)]">
                  <p className="text-sm">
                    Authentication Type: <strong>{instance.authenticationType}</strong>
                    <br />
                    Credentials are securely stored and managed by the system.
                  </p>
                </div>
              )}
            </FormSection>

            <ConnectorTestPanel
              instanceId={instanceId!}
              connectorName={instance.connector?.name || 'Connector'}
            />

            <div className="flex justify-end mt-6">
              <Button type="button" variant="default" size="sm" onClick={handleSave} disabled={saving} data-testid="save-connector-instance-btn">
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          </div>
        )}

        {/* Settings Tab */}
        {activeTab === 'settings' && (
          <div role="tabpanel" className="page-detail-layout__panel" data-testid="connector-instance-panel-settings">
            {instance.connector ? (
              <ConnectorConfigRenderer
                connector={instance.connector}
                instanceId={instanceId}
                settingsJson={settingsJson}
                isConnected={connectionStatus === 'Connected'}
                level="Instance"
                onChange={setSettingsJson}
                onConnected={() => {
                  loadInstance();
                  addNotification('Settings updated successfully', 'success');
                }}
              />
            ) : (
              <SettingsForm
                settingsJson={settingsJson}
                level="Instance"
                onChange={setSettingsJson}
              />
            )}
            
            <div className="flex justify-end mt-6">
              <Button type="button" variant="default" size="sm" onClick={handleSave} disabled={saving} data-testid="save-connector-instance-btn">
                {saving ? 'Saving...' : 'Save Settings'}
              </Button>
            </div>
          </div>
        )}

        {/* Capabilities Tab */}
        {activeTab === 'capabilities' && (
          <div role="tabpanel" className="page-detail-layout__panel" data-testid="connector-instance-panel-capabilities">
            {instance.connector && (
              <CapabilityList
                capabilities={instance.connector.capabilities}
                enabledCapabilityIds={enabledCapabilityIds}
                onToggle={(capabilityId, enabled) => {
                  if (enabled) {
                    setEnabledCapabilityIds([...enabledCapabilityIds, capabilityId]);
                  } else {
                    setEnabledCapabilityIds(enabledCapabilityIds.filter((id) => id !== capabilityId));
                  }
                }}
                showDetails
              />
            )}
            <div className="flex justify-end mt-6">
              <Button type="button" variant="default" size="sm" onClick={handleSave} disabled={saving} data-testid="save-connector-instance-btn">
                {saving ? 'Saving...' : 'Save Capabilities'}
              </Button>
            </div>
          </div>
        )}

        {/* Rate Limits Tab */}
        {activeTab === 'rate-limits' && (
          <div role="tabpanel" className="page-detail-layout__panel" data-testid="connector-instance-panel-rate-limits">
            {rateLimitStatus && <RateLimitStatusCard status={rateLimitStatus} />}
            <FormSection title="Rate Limit Configuration" data-testid="connector-instance-section-rate-limit-configuration">
              <div className="flex flex-col gap-4">
                <div className="flex flex-col gap-1">
                  <label className="text-sm font-medium" htmlFor="connector-instance-rateLimitPerMinute">Per Minute</label>
                  <Input
                    id="connector-instance-rateLimitPerMinute"
                    type="number"
                    value={rateLimitPerMinute}
                    onChange={(e) => setRateLimitPerMinute(e.target.value ? Number(e.target.value) : '')}
                    data-testid="connector-instance-rateLimitPerMinute"
                  />
                  <span className="text-xs text-[var(--color-text-secondary)]">Leave empty to inherit from connector defaults</span>
                </div>
                <div className="flex flex-col gap-1">
                  <label className="text-sm font-medium" htmlFor="connector-instance-rateLimitPerHour">Per Hour</label>
                  <Input
                    id="connector-instance-rateLimitPerHour"
                    type="number"
                    value={rateLimitPerHour}
                    onChange={(e) => setRateLimitPerHour(e.target.value ? Number(e.target.value) : '')}
                    data-testid="connector-instance-rateLimitPerHour"
                  />
                  <span className="text-xs text-[var(--color-text-secondary)]">Leave empty to inherit from connector defaults</span>
                </div>
                <div className="flex flex-col gap-1">
                  <label className="text-sm font-medium" htmlFor="connector-instance-rateLimitPerDay">Per Day</label>
                  <Input
                    id="connector-instance-rateLimitPerDay"
                    type="number"
                    value={rateLimitPerDay}
                    onChange={(e) => setRateLimitPerDay(e.target.value ? Number(e.target.value) : '')}
                    data-testid="connector-instance-rateLimitPerDay"
                  />
                  <span className="text-xs text-[var(--color-text-secondary)]">Leave empty to inherit from connector defaults</span>
                </div>
              </div>
            </FormSection>
            <div className="flex justify-end">
              <Button type="button" variant="default" size="sm" onClick={handleSave} disabled={saving} data-testid="save-connector-instance-btn">
                {saving ? 'Saving...' : 'Save Rate Limits'}
              </Button>
            </div>
          </div>
        )}

        {/* Usage & Analytics Tab */}
        {activeTab === 'usage' && (
          <div role="tabpanel" className="page-detail-layout__panel" data-testid="connector-instance-panel-usage">
            {usageStats ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <FormSection title="Usage Statistics" data-testid="connector-instance-section-usage-statistics">
                  <div className="flex flex-col gap-4">
                    <div>
                      <span className="text-xs text-[var(--color-text-secondary)]">Total Calls</span>
                      <h4 className="text-xl font-semibold">{usageStats.totalCalls.toLocaleString()}</h4>
                    </div>
                    <div>
                      <span className="text-xs text-[var(--color-text-secondary)]">Successful Calls</span>
                      <p className="text-xl font-semibold text-green-600">{usageStats.successfulCalls.toLocaleString()}</p>
                    </div>
                    <div>
                      <span className="text-xs text-[var(--color-text-secondary)]">Failed Calls</span>
                      <p className="text-xl font-semibold text-red-600">{usageStats.failedCalls.toLocaleString()}</p>
                    </div>
                    <div>
                      <span className="text-xs text-[var(--color-text-secondary)]">Average Duration</span>
                      <p className="text-xl font-semibold">{usageStats.averageDurationMs.toFixed(0)}ms</p>
                    </div>
                    {usageStats.totalCost > 0 && (
                      <div>
                        <span className="text-xs text-[var(--color-text-secondary)]">Total Cost</span>
                        <p className="text-xl font-semibold">${usageStats.totalCost.toFixed(2)}</p>
                      </div>
                    )}
                  </div>
                </FormSection>
                <FormSection title="Success Rate" data-testid="connector-instance-section-success-rate">
                  <div className="text-center py-8">
                    <p className="text-4xl font-bold text-green-600">
                      {usageStats.totalCalls > 0
                        ? ((usageStats.successfulCalls / usageStats.totalCalls) * 100).toFixed(1)
                        : 0}
                      %
                    </p>
                  </div>
                </FormSection>
              </div>
            ) : (
              <div className="rounded-lg bg-[var(--color-bg-inset)] p-4 text-[var(--color-text-secondary)]">
                <p className="text-sm">No usage data available yet.</p>
              </div>
            )}
          </div>
        )}

        {/* Webhooks Tab */}
        {activeTab === 'webhooks' && instance.webhooks && instance.webhooks.length > 0 && (
          <div role="tabpanel" className="page-detail-layout__panel" data-testid="connector-instance-panel-webhooks">
            <div className="rounded-lg bg-[var(--color-bg-inset)] p-4 text-[var(--color-text-secondary)]">
              <p className="text-sm">Webhook management coming soon.</p>
            </div>
          </div>
        )}
      </PageContainer>
    </div>
  );
};
